import React, { useState } from "react";
import { ScrollText } from "lucide-react";
import { fmtDateTime, schApi, schPref } from "@/lib/schApi";
import { Empty, ErrorBox, Loading, field, useLoad } from "@/components/nds/windows/finance/parts";

const ACTIONS = ["", "create", "update", "cancel", "complete", "override", "warning_confirmed"];
const TRACKED = ["title", "event_type", "location", "start_time", "end_time", "status", "max_capacity", "current_enrollment", "assigned_resident_ids", "assigned_staff_ids", "visibility", "is_mandatory", "notes", "cancel_reason"];

function Diff({ before, after }) {
  const keys = TRACKED.filter((k) => JSON.stringify(before?.[k]) !== JSON.stringify(after?.[k]));
  if (!before) return <div className="text-[11px] text-nex-muted">Created with {TRACKED.filter((k) => after?.[k] != null && after[k] !== "").length} fields.</div>;
  if (!keys.length) return <div className="text-[11px] text-nex-muted">No field changes.</div>;
  return (
    <table className="text-[11px] w-full"><tbody>
      {keys.map((k) => (
        <tr key={k} className="align-top"><td className="pr-2 text-nex-muted w-32">{k}</td>
          <td className="pr-2 text-red-300/80 line-through break-all">{JSON.stringify(before?.[k])}</td>
          <td className="text-emerald-300 break-all">{JSON.stringify(after?.[k])}</td></tr>
      ))}
    </tbody></table>
  );
}

/** Read-only immutable audit trail with before/after snapshots. */
export default function SchAuditContent() {
  const [action, setActionState] = useState(() => schPref.get("audit_action", ""));
  const setAction = (v) => { schPref.set("audit_action", v); setActionState(v); };
  const [open, setOpen] = useState(null);
  const [state, load] = useLoad(() => schApi.get("/audit", action ? { action } : {}), [action]);
  return (
    <div data-testid="sch-audit" className="flex flex-col h-full">
      <div className="px-5 py-3 border-b border-white/10 flex items-center gap-3">
        <ScrollText className="h-4 w-4 text-nex-gold" />
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold">Immutable audit log</span>
        <select data-testid="sch-audit-action" className={`${field} ml-auto`} value={action} onChange={(e) => setAction(e.target.value)}>
          {ACTIONS.map((a) => <option key={a} value={a}>{a ? a.replace("_", " ") : "All actions"}</option>)}
        </select>
      </div>
      {state.loading && !state.data ? <Loading testid="sch-audit-loading" /> : state.error ? <ErrorBox testid="sch-audit-error" error={state.error} onRetry={load} /> : (
        <ul className="flex-1 overflow-y-auto divide-y divide-white/5" data-testid="sch-audit-list">
          {state.data.rows.map((r) => (
            <li key={r.id} data-testid={`sch-audit-row-${r.id}`}>
              <button type="button" onClick={() => setOpen(open === r.id ? null : r.id)} className="w-full text-left px-5 py-2.5 flex items-center gap-3 hover:bg-white/[0.02]">
                <span className="font-mono text-[10px] text-nex-muted w-40 shrink-0">{fmtDateTime(r.at)}</span>
                <span className={`font-mono text-[10px] uppercase w-28 shrink-0 ${r.action === "override" ? "text-red-300" : r.action === "warning_confirmed" ? "text-amber-300" : "text-nex-gold"}`}>{r.action.replace("_", " ")}</span>
                <span className="flex-1 truncate text-nex-silver text-sm">{r.event_title}</span>
                <span className="text-xs text-nex-muted">{r.actor?.name}</span>
              </button>
              {open === r.id && <div className="px-5 pb-3 space-y-1">{r.note && <div className="text-xs text-white/80">{r.note}</div>}<Diff before={r.before} after={r.after} /></div>}
            </li>
          ))}
          {!state.data.rows.length && <li><Empty testid="sch-audit-empty">No audit rows yet.</Empty></li>}
        </ul>
      )}
    </div>
  );
}
